/**
 * URL ↔ Navigation state mapping.
 *
 * The store remains the source of truth for `(currentApp, currentPage,
 * activeEngagementId)`. The URL is a serialized view of that tuple, kept in
 * sync by `RouteSync` (URL → store on browser nav, store → URL on app nav).
 *
 * Path scheme — keep it short, human-readable, and reversible:
 *
 *   /focus                                       → workspace / focus tab
 *   /workspace                                   → workspace / home dashboard
 *   /workspace/portfolio                         → portfolio grid
 *   /workspace/engagements/:engId                → engagement landing
 *   /workspace/engagements/:engId/task/:taskId   → engagement task detail
 *   /clients                                     → clients dashboard
 *   /agents                                      → agents dashboard
 *   /agents/:pageId                              → agent detail page
 *   /pulse                                       → pulse dashboard
 *   /demo                                        → template gallery
 *   /demo/:templateName                          → single template preview
 *
 * Unknown paths route to the Clara chat default state.
 *
 * NOTE — the demo routes are handled by `RouteSync` as a special case
 * that bypasses store hydration. The store-driven branches in this
 * helper still mirror the engagement/workspace contract; the demo
 * surface is a separate top-level path.
 */
import { CONFIG } from '../constants';

export interface NavRouteState {
  app: string;
  page: string;
  engagementId: string | null;
  /** Task ID — only set on engagement task-detail routes. */
  taskId?: string | null;
}

const APP_WORKSPACE = CONFIG.WORKSPACE;       // 'kci-workspaces'
const APP_AGENTS = CONFIG.AGENTS;            // 'kci-agents'
const APP_CLARA = CONFIG.CLARA_APP;          // 'tru-clara-app'

const HOME = CONFIG.FIXED_TAB_HOME;           // '__home__'
const PORTFOLIO = CONFIG.PAGE_PORTFOLIO;      // '__portfolio__'
const CLARA_AI_CHAT = CONFIG.FIXED_TAB_CLARA_AI_CHAT; // '__clara-ai-chat__'
const EXECUTION_STREAM = '__execution-stream__';

/** Canonical default state — used on `/` and unknown paths. */
export const DEFAULT_ROUTE_STATE: NavRouteState = {
  app: APP_CLARA,
  page: CLARA_AI_CHAT,
  engagementId: null,
};

/**
 * Derive a URL pathname from navigation state.
 * Pure function — no side effects, deterministic.
 */
export function urlFromState(state: NavRouteState): string {
  const { app, page, engagementId } = state;

  if (app === APP_CLARA) {
    if (page === CLARA_AI_CHAT) return '/#/chat';
    // Task-detail pages carry their engId + taskId in the page id itself
    // (synthesized in stateFromUrl as `task-<engId>-<taskId>`). Extract
    // them so the round-trip URL gets the right suffix.
    const taskParsed = parseTaskPageId(page);
    if (taskParsed) {
      return `/workspace/engagements/${taskParsed.engId}/task/${taskParsed.taskId}`;
    }
    // Engagement-scoped page → route under /workspace/engagements/:engId.
    if (engagementId) return `/workspace/engagements/${engagementId}`;
    // Other tru-clara-ai pages (canvas-eng-*, ws-new-*, etc.) — keep
    // workspace root for now; deep-link IDs land in a later phase.
    return '/workspace';
  }
  

if (app === APP_WORKSPACE) {
  if (page === HOME) return '/workspace';
  if (page === PORTFOLIO) return '/workspace/portfolio';
  if (page === EXECUTION_STREAM) return '/workspace/execution-stream';
  return '/workspace';
}

  if (app === APP_AGENTS) {
    /* if (page === HOME || page === CONFIG.PAGE_ACTION_TRAY) return '/agents';
    const newTaskMatch = page.match(/^([a-z0-9-]+)__new$/);
    if (newTaskMatch) {
      return `/agents/${newTaskMatch[1]}/new`;
    } */
    return '/dashboard';
  }
  console.log(`urlFromState: unknown app/page combination: ${app}/${page}`);
  return page;
}

/**
 * Derive navigation state from a URL pathname.
 * Returns `DEFAULT_ROUTE_STATE` for unknown paths so the app never sits
 * in an unrenderable state.
 */
export function stateFromUrl(pathname: string): NavRouteState {
  const hashIndex = pathname.indexOf('#');
  const hashRoute = hashIndex >= 0 ? pathname.slice(hashIndex + 1) : '';
  const pathBeforeHash = hashIndex >= 0 ? pathname.slice(0, hashIndex) : pathname;
  const routeSource = hashRoute.startsWith('/chat') ? hashRoute : pathBeforeHash;

  // Normalise — strip query string + trailing slash, lower-case.
  const clean = routeSource.split('?')[0].replace(/\/+$/, '').toLowerCase() || '/';

  if (clean === '/' || clean === '/chat') {
    return { app: APP_CLARA, page: CLARA_AI_CHAT, engagementId: null };
  }

  if (clean === '/workspace') {
    return { app: APP_WORKSPACE, page: HOME, engagementId: null };
  }

  if (clean === '/workspace/portfolio') {
    return { app: APP_WORKSPACE, page: PORTFOLIO, engagementId: null };
  }

  if (clean === '/workspace/execution-stream') {
    return { app: APP_WORKSPACE, page: EXECUTION_STREAM, engagementId: null };
  }

  if (clean === '/agents') {
    return { app: APP_AGENTS, page: HOME, engagementId: null };
  }

  // Agents dashboard route.
  if (clean === '/dashboard') {
    return { app: APP_AGENTS, page: HOME, engagementId: null };
  }

  /* const agentNewTaskMatch = clean.match(/^\/agents\/([a-z0-9-]+)\/new$/);
  if (agentNewTaskMatch?.[1]) {
    return { app: APP_AGENTS, page: `${agentNewTaskMatch[1]}__new`, engagementId: null };
  }

  const agentPageMatch = clean.match(/^\/agents\/([a-z0-9-]+)$/);
  if (agentPageMatch?.[1]) {
    const page = agentPageMatch[1] === 'agent-dashboard' ? HOME : agentPageMatch[1];
    return { app: APP_AGENTS, page, engagementId: null };
  } */

  return DEFAULT_ROUTE_STATE;
}

/**
 * True if two states map to the same URL. Used by RouteSync to avoid
 * dispatching no-op updates that could cascade into a re-render loop.
 */
export function statesEqual(a: NavRouteState, b: NavRouteState): boolean {
  return urlFromState(a) === urlFromState(b);
}

/**
 * Parse a synthetic `task-<engId>__<taskId>` page id back into its parts.
 * Returns `null` if the page id doesn't match the pattern.
 *
 * The `__` separator is load-bearing — both engId and taskId carry their
 * own internal single dashes (e.g. `eng-pink-garments` paired with
 * `risk-assessment`), so a single-dash separator would be ambiguous.
 */
export function parseTaskPageId(page: string): { engId: string; taskId: string } | null {
  const match = page.match(/^task-(eng-[a-z0-9-]+)__([a-z][a-z0-9-]*)$/);
  if (!match) return null;
  return { engId: match[1], taskId: match[2] };
}

/**
 * Parse synthetic agent new-task page id: `<agentPageId>__new`.
 */
export function parseAgentNewTaskPageId(page: string): string | null {
  const match = page.match(/^([a-z0-9-]+)__new$/);
  if (!match) return null;
  return match[1];
}
